import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

/**
 * Botão usado na paginação.
 * 
 * O evento de `onClick` deve, obrigatoriamente, receber o número 
 * de página.
 * 
 * @param {{
 *   children: React.ReactNode,
 *   className?: string|string[]|{[key: string]: boolean},
 *   disabled?: boolean,
 *   page: number,
 *   onClick?: (value: number) => void,
 * }} props
 */
const PaginateButton = React.memo(({
  children,
  className, 
  disabled,
  page, 
  onClick
}) => {
  const _attr = {
    onClick: () => (onClick) ? onClick(page) : console.log(page),
    disabled
  };
  if (className) _attr.className = classnames(className);

  return (
    <button {..._attr}>
      {children}
    </button>
  );
});

PaginateButton.defaultProps = {
  children: null,
  className: null,
  disabled: false,
  onClick: null
};

PaginateButton.propTypes = {
  children: PropTypes.node,
  className: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.objectOf(PropTypes.bool)
  ]),
  disabled: PropTypes.bool,
  page: PropTypes.number.isRequired,
  onClick: PropTypes.func
};

/**
 * Componente de paginação genérico.
 * 
 * Únicas props obrigatórias são: `page`, `totalItems` e `onClick`, que 
 * é uma function que recebe o valor de página desejado ao ser executada.
 * 
 * Parâmetros:
 * - `page`: página atual
 * - `perPage`: itens por página
 * - `totalItems`: total de items a serem paginados
 * - `onClick`: evento de clique para pagina
 * - `showNext`: exibir botão "Próxima"
 * - `showPrev`: exibir botão "Anterior"
 * - `showFirst`: exibir botão "Primeira Página" 
 * - `showLast`: exibir botão "Última Página"
 * - `showText`: exibir texto "X de XX"
 * - `className`: classe do container de paginação
 * - `buttonClassName`: classe do botão de paginação
 * - `displayClassName`: classe do texto de páginas
 * - `nextText`: texto do botão "Próxima"
 * - `prevText`: texto do botão "Anterior"
 * - `firstText`: texto do botão "Primeira Página" 
 * - `lastText`: texto do botão "Última Página"
 * 
 * @param {{
 *   page: number,
 *   perPage?: number,
 *   totalItems: string,
 *   onClick: (page: number) => void,
 *   showNext?: boolean,
 *   showPrev?: boolean,
 *   showFirst?: boolean,
 *   showLast?: boolean,
 *   showText?: boolean,
 *   className?: string|string[]|{[key: string]: boolean},
 *   buttonClassName?: string|string[]|{[key: string]: boolean},
 *   displayClassName?: string|string[]|{[key: string]: boolean},
 *   nextText?: string,
 *   prevText?: string,
 *   firstText?: string,
 *   lastText?: string
 * }} props 
 */
export const Paginate = React.memo(({
  page, 
  perPage, 
  totalItems, 
  onClick, 
  showNext, 
  showPrev, 
  showFirst, 
  showLast, 
  showText,
  className,
  buttonClassName,
  displayClassName,
  nextText, 
  prevText, 
  firstText, 
  lastText
}) => {
  const _attr = {};
  if (className) _attr.className = classnames(className);

  const totalPages = Math.ceil(totalItems / perPage);
  const prev = (page > 1) ? page - 1 : page;
  const next = (page < totalPages) ? page + 1 : totalPages;

  return (
    <div {..._attr}>
      {showFirst 
        && <PaginateButton 
              page={1} 
              disabled={page === 1}
              className={buttonClassName}
              onClick={onClick}>{firstText}</PaginateButton>}
      
      {showPrev 
        && <PaginateButton 
              page={prev} 
              disabled={page === 1}
              className={buttonClassName}
              onClick={onClick}>{prevText}</PaginateButton>}

      {(() => {
        if (showText) {
          const _textAttr = {};
          if (displayClassName) {
            _textAttr.className = classnames(displayClassName);
          }

          return (
            <div {..._textAttr}>
              {page} de {totalPages}
            </div>
          );
        }
      })()}

      {showNext 
        && <PaginateButton 
              page={next} 
              disabled={page === totalPages}
              className={buttonClassName}
              onClick={onClick}>{nextText}</PaginateButton>}

      {showLast
        && <PaginateButton 
              page={totalPages} 
              disabled={page === totalPages}
              className={buttonClassName}
              onClick={onClick}>{lastText}</PaginateButton>}
    </div>
  );
});

Paginate.defaultProps = {
  page: 1,
  perPage: 12,
  showNext: true,
  showPrev: true,
  showFirst: true,
  showLast: true,
  showText: true, 
  className: "",
  buttonClassName: "",
  displayClassName: "",
  nextText: ">",
  prevText: "<",
  firstText: "<<",
  lastText: ">>"
}

Paginate.propTypes = {
  page: PropTypes.number.isRequired,
  perPage: PropTypes.number,
  totalItems: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired, 
  showNext: PropTypes.bool,
  showPrev: PropTypes.bool,
  showFirst: PropTypes.bool,
  showLast: PropTypes.bool,
  showText: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.objectOf(PropTypes.bool)
  ]),
  buttonClassName: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.objectOf(PropTypes.bool)
  ]),
  displayClassName: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.objectOf(PropTypes.bool)
  ]),
  nextText: PropTypes.string,
  prevText: PropTypes.string,
  firstText: PropTypes.string,
  lastText: PropTypes.string
};
